

import { useState, useEffect } from "react";
import { useLocation } from 'react-router-dom';
import { Download, X, Smartphone, Monitor } from "lucide-react";
import { usePWA } from "@/hooks/usePWA";
import { useSettings } from "@/context/SettingsContext";

const DISMISSAL_DURATION_DAYS = 7;
const STORAGE_KEY = "pwa-prompt-dismissed-v2";

// Routes where PWA prompt SHOULD show (only login page for install prompt)
const SHOW_ON_ROUTES = ['/login'];

export function PWAInstallPrompt() {
  const { isInstallable, isStandalone, promptInstall } = usePWA();
  const { settings } = useSettings();
  const { businessName } = settings.business;
  const { appName, shortName, installPromptEnabled } = settings.pwa;
  
  // Use PWA app name from settings or fallback to business name
  const displayAppName = appName || businessName || "Shalimar ERP";
  const displayShortName = shortName || displayAppName.split(' ')[0] || "Shalimar";
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Check if current page should show the prompt (only login page)
  const shouldShowPrompt = SHOW_ON_ROUTES.includes(location.pathname);

  // Check if prompt was recently dismissed
  useEffect(() => {
    const checkDismissal = () => {
      const dismissed = localStorage.getItem(STORAGE_KEY);
      if (dismissed) {
        const dismissedTime = parseInt(dismissed, 10);
        const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
        if (daysSinceDismissed < DISMISSAL_DURATION_DAYS) {
          setIsDismissed(true);
        } else {
          // Clear expired dismissal
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    };

    checkDismissal();
  }, []);

  // Show prompt if:
  // - installable
  // - not standalone
  // - not dismissed
  // - on login page only
  // - install prompt enabled in settings
  useEffect(() => {
    if (isInstallable && !isStandalone && !isDismissed && shouldShowPrompt && installPromptEnabled !== false) {
      // Small delay to not interrupt initial page load
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isInstallable, isStandalone, isDismissed, shouldShowPrompt, installPromptEnabled]);

  const handleInstall = async () => {
    try {
      const installed = await promptInstall();
      if (installed) {
        setIsVisible(false);
      }
      // If not installed (false), keep prompt visible for user to try again or dismiss
    } catch (err) {
      console.error("Install failed:", err);
      // Don't log error to console - it's expected on unsupported browsers
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
  };

  // Don't render if not installable, already installed, or dismissed
  if (!isInstallable || isStandalone || !isVisible) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50 p-3 bg-gradient-to-r from-amber-500 to-amber-600 shadow-lg animate-in slide-in-from-top">
      <div className="flex items-center justify-between max-w-4xl mx-auto">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
            <Smartphone className="w-5 h-5 text-white hidden sm:block" />
            <Monitor className="w-5 h-5 text-white sm:hidden" />
          </div>
          <div className="text-white">
            <p className="font-semibold text-sm">Install {displayShortName}</p>
            <p className="text-xs text-white/80 hidden sm:inline">Add to home screen for quick access</p>
            <p className="text-xs text-white/80 sm:hidden">Quick access to your dashboard</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleInstall}
            className="flex items-center gap-1.5 px-4 py-2 bg-white text-amber-600 rounded-lg text-sm font-semibold hover:bg-white/90 transition-colors shadow-sm"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Install App</span>
            <span className="sm:hidden">Install</span>
          </button>
          <button
            onClick={handleDismiss}
            className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
